<template>
  <button
    :type="type"
    class="btn"
    :class="[`btn--${variant}`, { 'btn--block': block, 'is-loading': loading }]"
    :disabled="disabled || loading"
    @click="$emit('click', $event)"
  >
    <span class="btn__label"><slot /></span>
    <span v-if="loading" class="btn__spinner" aria-hidden="true" />
  </button>
</template>

<script setup>
defineProps({
  variant: { type: String, default: 'primary' }, // primary | secondary | ghost | pill
  block: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  type: { type: String, default: 'button' },
})
defineEmits(['click'])
</script>

<style lang="scss" scoped>
.btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-family: var(--font-brand);
  font-weight: var(--fw-bold);
  cursor: pointer;
  transition:
    transform var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out),
    background-color var(--dur-fast) var(--ease-out);
  -webkit-tap-highlight-color: transparent;

  &--block {
    display: flex;
    width: 100%;
  }

  &:disabled {
    cursor: default;
  }

  // Primary — chunky 3D blue CTA ("Draai aan het rad")
  &--primary {
    height: 57px;
    padding: 0 var(--sp-5);
    font-size: var(--fs-2xl);
    color: var(--c-white);
    background: var(--c-blue);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-cta);

    &:active:not(:disabled) {
      transform: translateY(6px);
      box-shadow: 0 3px 0 0 var(--c-blue-deep);
    }
    &:disabled {
      background: var(--c-disabled);
      box-shadow: 0 9px 0 0 #9aa0a6;
    }
  }

  // CTA — flat deep-blue full-width primary (shop/opt-in/eind/intro/reveal)
  &--cta {
    height: 60px;
    padding: 0 var(--sp-5);
    font-size: var(--fs-xl);
    color: var(--c-white);
    background: var(--c-blue-deep);
    border-radius: var(--r-lg);

    &:disabled {
      opacity: 0.55;
    }
  }

  // Light — white full-width button with dark text (in-game CTAs)
  // Figma 393:269 / 393:428: 342×69, radius 18, 23px bold black
  &--light {
    height: 69px;
    padding: 0 var(--sp-5);
    font-size: var(--fs-2xl);
    color: var(--c-ink);
    background: var(--c-white);
    border-radius: var(--r-lg);

    &:disabled {
      opacity: 0.55;
    }
  }

  // Secondary — solid deep-blue pill ("Inwisselen")
  &--secondary {
    height: 40px;
    padding: 0 var(--sp-4);
    font-size: var(--fs-base);
    font-weight: var(--fw-semibold);
    color: var(--c-white);
    background: var(--c-blue-deep);
    border-radius: var(--r-badge);
  }

  // Ghost — outline on gradient/light
  &--ghost {
    height: 57px;
    padding: 0 var(--sp-5);
    font-size: var(--fs-lg);
    color: var(--c-ink);
    background: transparent;
    border: 2px solid currentColor;
    border-radius: var(--r-lg);
  }

  // Pill — compact
  &--pill {
    height: 36px;
    padding: 0 var(--sp-4);
    font-size: var(--fs-sm);
    color: var(--c-ink);
    background: var(--c-white);
    border: 1px solid var(--c-border);
    border-radius: var(--r-pill);
  }

  &.is-loading .btn__label {
    opacity: 0;
  }
}

.btn__spinner {
  position: absolute;
  width: 22px;
  height: 22px;
  border: 3px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: btn-spin 0.8s linear infinite;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
